<script setup lang="ts">
import type { LocationOpeningHours } from '~layers/.ts'

const props = defineProps<{ location: LocationOpeningHours }>()

const openingHourLocations = computed(() => {
  return [
    {
      id: 'store',
      label: 'Winkel',
      data: props.location,
    },
    ...props.location.warehouses.map((warehouse: any) => {
      return {
        id: warehouse.location.code,
        label: 'Magazijn',
        data: warehouse,
      }
    }),
  ]
})

const selected = ref('store')
const nextWeek = ref(false)

const openingHoursToShow = computed(() => openingHourLocations.value.find((item: any) => selected.value === item.id)?.data || null)

function cleanAddressPart(value: unknown) {
  const text = typeof value === 'string' ? value.trim() : ''
  if (!text || ['null', 'undefined', '-'].includes(text.toLowerCase())) {
    return ''
  }
  return text
}

const stockroomAddress = computed(() => {
  if (selected.value === 'store') {
    return ''
  }

  const location = openingHoursToShow.value?.location
  const addressLine = [location?.locationLine1, location?.locationLine2]
    .map(cleanAddressPart)
    .filter(Boolean)
    .join(' ')

  if (!addressLine) {
    return ''
  }

  const city = cleanAddressPart(location?.city)
  return city ? `${addressLine}, ${city}` : addressLine
})
</script>

<template>
  <div class="store-detail__panel-section">
    <UiHeading force-tag="div">
      {{ $t('Opening hours') }}
    </UiHeading>

    <div class="opening-hours__toggle">
      <template
        v-for="loc in openingHourLocations"
        :key="loc.id"
      >
        <input
          :id="loc.id"
          v-model="selected"
          name="week"
          type="radio"
          :value="loc.id"
        >
        <label :for="loc.id">{{ loc.label }}</label>
      </template>
    </div>

    <client-only>
      <div class="store-detail__normal-hours">
        <div class="opening-hours">
          <table class="opening-hours__table">
            <tbody>
              <template
                v-for="(day, index) in openingHoursToShow.days.slice(nextWeek ? 7 : 0, nextWeek ? 14 : 7)"
                :key="index"
              >
                <tr
                  class="opening-hours-row"
                  :class="{
                    'opening-hours-row__today': !index && !nextWeek,
                  }"
                >
                  <td class="opening-hours-row__day">
                    {{ useTitle(String($t(day.dayName))) }}
                  </td>
                  <td class="opening-hours-row__date">
                    {{ day.dayOfMonth }} {{ $t(day.monthNameShort) }}
                  </td>
                  <td
                    v-if="day.isOpen && day.slots.length"
                    class="opening-hours-row__times"
                  >
                    <template
                      v-for="(slot, slotIndex) in day.slots"
                      :key="slotIndex"
                    >
                      {{ slot.open }} - {{ slot.close }} <br>
                    </template>
                  </td>
                  <td
                    v-else
                    class="opening-hours-row__times"
                  >
                    {{ $t('Closed') }}
                  </td>
                </tr>
              </template>
            </tbody>
          </table>
          <button
            class="opening-hours__more"
            @click="nextWeek = !nextWeek"
          >
            <NuxtIcon name="icon:clock" />
            <span>
              {{ $t(nextWeek ? 'View this week' : 'View next week') }}
            </span>
          </button>
        </div>
      </div>

      <div
        v-if="openingHoursToShow.exceptions && openingHoursToShow.exceptions.length"
        class="store-detail__special-hours"
      >
        <div class="heading">
          {{ $t('Special opening hours') }}
        </div>
        <div class="opening-hours">
          <table class="opening-hours__table">
            <tbody>
              <template
                v-for="(day, index) in location.exceptions"
                :key="index"
              >
                <tr
                  class="opening-hours-row"
                >
                  <td class="opening-hours-row__day">
                    {{ day.reason ? $t(day.reason) : '' }}
                  </td>
                  <td
                    v-if="day.startDate.dateString !== day.endDate.dateString"
                    class="opening-hours-row__date"
                  >
                    {{ day.startDate.dayOfMonth }} {{ $t(day.startDate.monthName) }} -<br>
                    {{ day.endDate.dayOfMonth }} {{ $t(day.endDate.monthName) }} <br>
                  </td>
                  <td
                    v-else
                    class="opening-hours-row__date"
                  >
                    {{ day.startDate.dayOfMonth }} {{ $t(day.startDate.monthNameShort) }}
                  </td>
                  <td
                    v-if="day.isOpen"
                    class="opening-hours-row__times"
                  >
                    <template
                      v-for="(slot, slotIndex) in day.slots"
                      :key="slotIndex"
                    >
                      {{ slot.open }} - {{ slot.close }} <br>
                    </template>
                  </td>
                  <td
                    v-else
                    class="opening-hours-row__times"
                  >
                    {{ $t('Closed') }}
                  </td>
                </tr>
              </template>
            </tbody>
          </table>
        </div>
      </div>
    </client-only>

    <div
      v-if="selected !== 'store'"
      class="stockroom"
    >
      <div
        v-if="stockroomAddress"
        class="heading stockroom__heading"
      >
        {{ $t('Adres magazijn') }}
      </div>
      <div
        v-if="stockroomAddress"
        class="stockroom__description"
      >
        <p>
          {{ stockroomAddress }}
        </p>
        <a
          class="stockroom__link"
          :href="`https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(stockroomAddress)}`"
          target="_blank"
        > {{ $t('Plan your route') }}
        </a>
      </div>
      <div class="stockroom__appointment">
        <NuxtIcon name="icon:appointment" />
        <span>{{ $t('Afhalen alleen op afspraak') }}</span>
      </div>
    </div>
  </div>
</template>
